import { useRef, useState, useEffect, useCallback } from 'react';
import { getShapeBoundingBox as getShapeBoundingBoxUtil } from '../components/shapes';

const getShapeBoundingBox = (s) => getShapeBoundingBoxUtil(s);

export const useWhiteboardGroupSelect = (
  shapes, setShapes,
  selectedShape, setSelectedShape,
  setDragged,
  pan, svgRef, tool,
  shouldIgnoreKeyboardEvent,
) => {
  const [selectionBox, setSelectionBox] = useState(null);
  const [selectedShapes, setSelectedShapes] = useState([]);
  const selectionStartRef = useRef(null);
  const isBoxSelectingRef = useRef(false);

  const [isGroupDragging, setIsGroupDragging] = useState(false);
  const groupDragStart = useRef(null);

  const [isGroupResizing, setIsGroupResizing] = useState(false);
  const groupResizeStart = useRef(null);

  const isMouseInSelectionArea = useCallback((position) => {
    if (!position || selectedShapes.length === 0) return false;
    const group = shapes.filter(s => selectedShapes.includes(s.id));
    if (group.length === 0) return false;
    const xs = group.map(s => getShapeBoundingBox(s).x);
    const ys = group.map(s => getShapeBoundingBox(s).y);
    const ws = group.map(s => getShapeBoundingBox(s).x + getShapeBoundingBox(s).width);
    const hs = group.map(s => getShapeBoundingBox(s).y + getShapeBoundingBox(s).height);
    const minX = Math.min(...xs), minY = Math.min(...ys);
    const maxX = Math.max(...ws), maxY = Math.max(...hs);
    return position.x >= minX && position.x <= maxX && position.y >= minY && position.y <= maxY;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedShapes, shapes]);

  function handleGroupMouseDown(e) {
    if (selectedShapes.length && tool === 'select') {
      const rect = e.currentTarget.getBoundingClientRect();
      const ecx = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
      const ecy = e.clientY ?? e.touches?.[0]?.clientY ?? 0;
      const clickX = (ecx - rect.left - pan.x) / pan.scale;
      const clickY = (ecy - rect.top - pan.y) / pan.scale;

      const selectedShapesData = shapes.filter(s => selectedShapes.includes(s.id));
      let isClickInSelectionArea = false;
      for (const shape of selectedShapesData) {
        const box = getShapeBoundingBox(shape);
        if (clickX >= box.x && clickX <= box.x + box.width &&
            clickY >= box.y && clickY <= box.y + box.height) {
          isClickInSelectionArea = true;
          break;
        }
      }

      if (!isClickInSelectionArea) {
        if (document.body.style.cursor === 'grabbing') document.body.style.cursor = 'default';
        return;
      }

      setIsGroupDragging(true);
      const selectedShapesDataCopy = selectedShapesData.map(s => ({ ...s }));
      if (selectedShapesDataCopy.length > 0) {
        groupDragStart.current = {
          x: (ecx - rect.left - pan.x) / pan.scale,
          y: (ecy - rect.top - pan.y) / pan.scale,
          shapes: selectedShapesDataCopy,
        };
        document.body.style.cursor = 'grabbing';
        e.stopPropagation();
      } else {
        setIsGroupDragging(false);
      }
    }
  }

  function handleGroupMouseMove(e) {
    // Group drag logic is handled globally in handleBoardMouseMove
  }

  function handleGroupMouseUp() {
    if (isGroupDragging) {
      setIsGroupDragging(false);
      groupDragStart.current = null;
      document.body.style.cursor = 'default';
    }
  }

  function handleGroupResizeMouseDown(e, corner) {
    if (tool === 'pen') return;
    if (selectedShapes.length && tool === 'select') {
      setIsGroupResizing(true);
      const svgRect = svgRef.current.getBoundingClientRect();
      const ecx = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
      const ecy = e.clientY ?? e.touches?.[0]?.clientY ?? 0;
      const startX = (ecx - svgRect.left - pan.x) / pan.scale;
      const startY = (ecy - svgRect.top - pan.y) / pan.scale;

      const group = shapes.filter(s => selectedShapes.includes(s.id));
      const xs = group.map(s => getShapeBoundingBox(s).x);
      const ys = group.map(s => getShapeBoundingBox(s).y);
      const ws = group.map(s => getShapeBoundingBox(s).x + getShapeBoundingBox(s).width);
      const hs = group.map(s => getShapeBoundingBox(s).y + getShapeBoundingBox(s).height);

      groupResizeStart.current = {
        corner,
        startX,
        startY,
        originalBox: { x: Math.min(...xs), y: Math.min(...ys), width: Math.max(...ws) - Math.min(...xs), height: Math.max(...hs) - Math.min(...ys) },
        shapes: group.map(s => ({ ...s })),
      };
      e.stopPropagation();
    }
  }

  function handleGroupResizeMouseMove(e) {
    if (isGroupResizing && groupResizeStart.current) {
      const svgRect = svgRef.current.getBoundingClientRect();
      const ecx = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
      const ecy = e.clientY ?? e.touches?.[0]?.clientY ?? 0;
      const currentX = (ecx - svgRect.left - pan.x) / pan.scale;
      const currentY = (ecy - svgRect.top - pan.y) / pan.scale;
      const dx = currentX - groupResizeStart.current.startX;
      const dy = currentY - groupResizeStart.current.startY;

      if (Math.abs(dx) < 5 && Math.abs(dy) < 5) return;

      const { corner, originalBox, shapes: originalShapes } = groupResizeStart.current;
      let newX = originalBox.x, newY = originalBox.y;
      let newWidth = originalBox.width, newHeight = originalBox.height;

      switch (corner) {
        case 'nw':
          newWidth = Math.max(20, originalBox.width - dx);
          newHeight = Math.max(20, originalBox.height - dy);
          newX = originalBox.x + originalBox.width - newWidth;
          newY = originalBox.y + originalBox.height - newHeight;
          break;
        case 'ne':
          newWidth = Math.max(20, originalBox.width + dx);
          newHeight = Math.max(20, originalBox.height - dy);
          newY = originalBox.y + originalBox.height - newHeight;
          break;
        case 'sw':
          newWidth = Math.max(20, originalBox.width - dx);
          newHeight = Math.max(20, originalBox.height + dy);
          newX = originalBox.x + originalBox.width - newWidth;
          break;
        case 'se':
          newWidth = Math.max(20, originalBox.width + dx);
          newHeight = Math.max(20, originalBox.height + dy);
          break;
        default:
          break;
      }

      const scale = Math.sqrt((newWidth / originalBox.width) * (newHeight / originalBox.height));

      setShapes(shapes => (shapes || []).map(s => {
        if (selectedShapes.includes(s.id)) {
          const orig = originalShapes.find(ss => ss.id === s.id);
          if (orig) {
            const anchorX = corner.includes('e') ? originalBox.x : originalBox.x + originalBox.width;
            const anchorY = corner.includes('s') ? originalBox.y : originalBox.y + originalBox.height;
            const newAnchorX = corner.includes('e') ? newX : newX + newWidth;
            const newAnchorY = corner.includes('s') ? newY : newY + newHeight;

            if (orig.type === 'line' || orig.type === 'arrow') {
              return { ...s, x1: newAnchorX + (orig.x1 - anchorX) * scale, y1: newAnchorY + (orig.y1 - anchorY) * scale, x2: newAnchorX + (orig.x2 - anchorX) * scale, y2: newAnchorY + (orig.y2 - anchorY) * scale };
            } else if (orig.type === 'pencil') {
              return { ...s, points: orig.points.map(([px, py]) => [newAnchorX + (px - anchorX) * scale, newAnchorY + (py - anchorY) * scale]) };
            } else if (orig.type === 'sticker') {
              return { ...s, x: newAnchorX + (orig.x - anchorX) * scale, y: newAnchorY + (orig.y - anchorY) * scale, w: orig.w * scale, h: orig.h * scale };
            } else if (orig.type === 'circle') {
              return { ...s, x: newAnchorX + (orig.x - anchorX) * scale, y: newAnchorY + (orig.y - anchorY) * scale, r: orig.r * scale };
            } else {
              return { ...s, x: newAnchorX + (orig.x - anchorX) * scale, y: newAnchorY + (orig.y - anchorY) * scale, width: orig.width * scale, height: orig.height * scale };
            }
          }
        }
        return s;
      }));
    }
  }

  function handleGroupResizeMouseUp() {
    setIsGroupResizing(false);
    groupResizeStart.current = null;
  }

  // Group delete / escape via keyboard
  useEffect(() => {
    function handleKeyDown(e) {
      if (shouldIgnoreKeyboardEvent && shouldIgnoreKeyboardEvent(e)) return;
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedShapes.length) {
        setShapes(shapes => (shapes || []).filter(s => !selectedShapes.includes(s.id)));
        setSelectedShapes([]);
      }
      if (e.key === 'Escape') {
        setSelectedShape(null);
        setDragged(null);
        setSelectedShapes([]);
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedShapes, shouldIgnoreKeyboardEvent]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    selectionBox, setSelectionBox,
    selectedShapes, setSelectedShapes,
    selectionStartRef, isBoxSelectingRef,
    isGroupDragging, setIsGroupDragging, groupDragStart,
    isGroupResizing, setIsGroupResizing, groupResizeStart,
    isMouseInSelectionArea,
    handleGroupMouseDown, handleGroupMouseMove, handleGroupMouseUp,
    handleGroupResizeMouseDown, handleGroupResizeMouseMove, handleGroupResizeMouseUp,
  };
};
